// src/pages/SettingsPage.js
// [marker: bk-register-frontend v1]
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import DashboardSettings from '../components/DashboardSettings'; // [marker: dashboard v1]
import LetterheadSettings from '../components/LetterheadSettings'; // [marker: finish-up v1]
import AssistantSettings from '../components/AssistantSettings'; // [marker: ai-assistant v1]
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('org');
  const { isSuperAdmin } = useAuth();
  const [settings, setSettings] = useState({});
  const [memberTypes, setMemberTypes] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [mtModal, setMtModal] = useState(null);   // membership type add/edit
  const [spModal, setSpModal] = useState(null);   // salesperson add/edit
  const [tplModal, setTplModal] = useState(null); // email template edit

  useEffect(() => {
    api.get('/settings/org').then(r => setSettings(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (activeTab === 'membership') api.get('/settings/membership-types').then(r => setMemberTypes(r.data));
    if (activeTab === 'salespersons') api.get('/settings/salespersons').then(r => setSalespersons(r.data));
    if (activeTab === 'templates') api.get('/settings/email-templates').then(r => setEmailTemplates(r.data));
    if (activeTab === 'holidays') api.get(`/settings/holidays?year=${new Date().getFullYear()}`).then(r => setHolidays(r.data));
  }, [activeTab]);

  const saveOrg = async () => {
    setSaving(true);
    try {
      await api.patch('/settings/org', {
        org_name: settings.org_name,
        org_address: settings.org_address,
        org_phone: settings.org_phone,
        org_email: settings.org_email,
        org_website: settings.org_website,
        grace_period_days: settings.grace_period_days,
        renewal_reminder_days_1: settings.renewal_reminder_days_1,
        renewal_reminder_days_2: settings.renewal_reminder_days_2,
        bookkeeping_allow_edit_generated: settings.bookkeeping_allow_edit_generated,
      });
      toast.success('Settings saved.');
    } catch { toast.error('Failed to save.'); }
    finally { setSaving(false); }
  };

  const syncHolidays = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/settings/holidays/sync-hebcal?year=${new Date().getFullYear()}`);
      toast.success(res.data.message);
      api.get(`/settings/holidays?year=${new Date().getFullYear()}`).then(r => setHolidays(r.data));
    } catch { toast.error('Sync failed.'); }
    finally { setSyncing(false); }
  };

  const reloadMemberTypes  = () => api.get('/settings/membership-types').then(r => setMemberTypes(r.data));
  const reloadSalespersons = () => api.get('/settings/salespersons').then(r => setSalespersons(r.data));
  const reloadTemplates    = () => api.get('/settings/email-templates').then(r => setEmailTemplates(r.data));

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
      </div>

      <div className="tabs">
        {['org','membership','salespersons','templates','holidays','letterhead','dashboard', ...(isSuperAdmin() ? ['assistant'] : [])].map(tab => (
          <button key={tab} className={`tab-btn${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'assistant' ? 'AI Assistant' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <DashboardSettings />}{/* [marker: dashboard v1] */}
      {activeTab === 'assistant' && isSuperAdmin() && <AssistantSettings />}{/* [marker: ai-assistant v1] */}
      {activeTab === 'letterhead' && <LetterheadSettings />}{/* [marker: finish-up v1] */}

      {activeTab === 'org' && (
        <div className="card" style={{ maxWidth: 700 }}>
          <div className="card-header"><span className="card-title">Organization Settings</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Organization Name</label>
                <input className="form-input" value={settings.org_name || ''} onChange={e => setSettings(s => ({...s, org_name: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={settings.org_email || ''} onChange={e => setSettings(s => ({...s, org_email: e.target.value}))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <input className="form-input" value={settings.org_address || ''} onChange={e => setSettings(s => ({...s, org_address: e.target.value}))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" value={settings.org_phone || ''} onChange={e => setSettings(s => ({...s, org_phone: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Website</label>
                <input className="form-input" value={settings.org_website || ''} onChange={e => setSettings(s => ({...s, org_website: e.target.value}))} />
              </div>
            </div>
            <hr className="divider" />
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Grace Period (days)</label>
                <input className="form-input" type="number" value={settings.grace_period_days || 90} onChange={e => setSettings(s => ({...s, grace_period_days: e.target.value}))} />
                <p className="form-hint">Days after renewal date before status changes to Lapsed</p>
              </div>
              <div className="form-group">
                <label className="form-label">First Reminder (days before)</label>
                <input className="form-input" type="number" value={settings.renewal_reminder_days_1 || 30} onChange={e => setSettings(s => ({...s, renewal_reminder_days_1: e.target.value}))} />
              </div>
            </div>
            <hr className="divider" />
            <div className="form-group">
              <label className="form-label">Bookkeeping</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', cursor: 'pointer' }}>
                <input type="checkbox"
                  checked={settings.bookkeeping_allow_edit_generated === 'true'}
                  onChange={e => setSettings(s => ({ ...s, bookkeeping_allow_edit_generated: e.target.checked ? 'true' : 'false' }))} />
                Allow editing of auto-generated transactions in the Bank Register
              </label>
              <p className="form-hint">Manually-added transactions can always be edited. Turn this on to also allow editing transactions created automatically from invoice/ticket payments, batch deposits, and expenses.</p>
            </div>
          </div>
          <div className="card-footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={saveOrg} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
          </div>
        </div>
      )}

      {activeTab === 'membership' && (
        <div className="card" style={{ maxWidth: 700 }}>
          <div className="card-header"><span className="card-title">Membership Types</span><button className="btn btn-primary btn-sm" onClick={() => setMtModal({})}>+ Add Type</button></div>
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Name</th><th>Annual Dues</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {memberTypes.map(mt => (
                  <tr key={mt.id}>
                    <td className="td-primary">{mt.name}</td>
                    <td>${parseFloat(mt.annual_dues).toLocaleString()}</td>
                    <td><span className={`badge badge-${mt.is_active ? 'active' : 'lapsed'}`}>{mt.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ textAlign: 'right' }}><button className="btn btn-ghost btn-sm" onClick={() => setMtModal(mt)}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'salespersons' && (
        <div className="card" style={{ maxWidth: 500 }}>
          <div className="card-header"><span className="card-title">Salespersons</span><button className="btn btn-primary btn-sm" onClick={() => setSpModal({})}>+ Add Salesperson</button></div>
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Initials</th><th>Full Name</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {salespersons.map(sp => (
                  <tr key={sp.id}>
                    <td className="td-primary td-mono">{sp.initials}</td>
                    <td>{sp.full_name}</td>
                    <td><span className={`badge badge-${sp.is_active ? 'active' : 'lapsed'}`}>{sp.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ textAlign: 'right' }}><button className="btn btn-ghost btn-sm" onClick={() => setSpModal(sp)}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Email Templates</span></div>
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Template</th><th>Subject</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {emailTemplates.map(t => (
                  <tr key={t.id}>
                    <td className="td-primary td-mono" style={{ fontSize: '0.78rem' }}>{t.template_key}</td>
                    <td style={{ fontSize: '0.875rem' }}>{t.subject}</td>
                    <td><span className={`badge badge-${t.is_active ? 'active' : 'lapsed'}`}>{t.is_active ? 'Active' : 'Off'}</span></td>
                    <td style={{ textAlign: 'right' }}><button className="btn btn-ghost btn-sm" onClick={() => setTplModal(t)}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'holidays' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <div className="card-header">
            <span className="card-title">Jewish Holiday Calendar {new Date().getFullYear()}</span>
            <button className="btn btn-secondary btn-sm" onClick={syncHolidays} disabled={syncing}>
              {syncing ? 'Syncing...' : '🔄 Sync from Hebcal'}
            </button>
          </div>
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Holiday</th><th>Date</th><th>Blocked</th></tr></thead>
              <tbody>
                {holidays.length === 0 ? (
                  <tr><td colSpan={3}><div className="empty-state" style={{ padding: 20 }}>
                    <p>No holidays loaded. Click "Sync from Hebcal" to import.</p>
                  </div></td></tr>
                ) : holidays.map(h => (
                  <tr key={h.id}>
                    <td className="td-primary">{h.holiday_name}</td>
                    <td style={{ fontSize: '0.875rem' }}>{fmtDate(h.holiday_date)}</td>
                    <td>{h.is_blocked ? <span className="badge badge-lapsed">Blocked</span> : <span className="badge badge-active">Allowed</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {mtModal && <MembershipTypeModal data={mtModal} onClose={() => setMtModal(null)} onSaved={() => { setMtModal(null); reloadMemberTypes(); }} />}
      {spModal && <SalespersonModal data={spModal} onClose={() => setSpModal(null)} onSaved={() => { setSpModal(null); reloadSalespersons(); }} />}
      {tplModal && <TemplateEditModal data={tplModal} onClose={() => setTplModal(null)} onSaved={() => { setTplModal(null); reloadTemplates(); }} />}
    </div>
  );
}

function MembershipTypeModal({ data, onClose, onSaved }) {
  const isEdit = !!data.id;
  const [form, setForm] = useState({ name: data.name || '', annualDues: data.annual_dues != null ? String(data.annual_dues) : '', isActive: data.is_active !== false });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim() || form.annualDues === '') return toast.error('Name and annual dues are required.');
    setSaving(true);
    try {
      const body = { name: form.name.trim(), annualDues: parseFloat(form.annualDues), isActive: form.isActive };
      if (isEdit) await api.patch(`/settings/membership-types/${data.id}`, body);
      else await api.post('/settings/membership-types', body);
      toast.success('Saved.'); onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header"><span className="modal-title">{isEdit ? 'Edit' : 'Add'} Membership Type</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div className="form-group"><label className="form-label">Name</label><input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Annual Dues</label><input className="form-input" type="number" step="0.01" value={form.annualDues} onChange={e => setForm(f => ({ ...f, annualDues: e.target.value }))} /></div>
          {isEdit && (<label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} /><span>Active</span></label>)}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button></div>
      </div>
    </div>
  );
}

function SalespersonModal({ data, onClose, onSaved }) {
  const isEdit = !!data.id;
  const [form, setForm] = useState({ initials: data.initials || '', fullName: data.full_name || '', email: data.email || '', isActive: data.is_active !== false });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.initials.trim() || !form.fullName.trim()) return toast.error('Initials and full name are required.');
    setSaving(true);
    try {
      const body = { initials: form.initials.trim(), fullName: form.fullName.trim(), email: form.email.trim() || null, isActive: form.isActive };
      if (isEdit) await api.patch(`/settings/salespersons/${data.id}`, body);
      else await api.post('/settings/salespersons', body);
      toast.success('Saved.'); onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header"><span className="modal-title">{isEdit ? 'Edit' : 'Add'} Salesperson</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div className="form-group"><label className="form-label">Initials</label><input className="form-input" value={form.initials} onChange={e => setForm(f => ({ ...f, initials: e.target.value }))} maxLength={8} /></div>
          <div className="form-group"><label className="form-label">Full Name</label><input className="form-input" value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
          {isEdit && (<label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} /><span>Active</span></label>)}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button></div>
      </div>
    </div>
  );
}

function TemplateEditModal({ data, onClose, onSaved }) {
  const [form, setForm] = useState({ templateName: data.template_name || '', subject: data.subject || '', isActive: data.is_active !== false });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.subject.trim()) return toast.error('Subject is required.');
    setSaving(true);
    try {
      await api.patch(`/settings/email-templates/${data.id}`, { templateName: form.templateName, subject: form.subject, isActive: form.isActive });
      toast.success('Saved.'); onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header"><span className="modal-title">Edit Template <span style={{ color: 'var(--gray-400)', fontSize: '0.8rem', fontWeight: 400, marginLeft: 6 }}>{data.template_key}</span></span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div className="form-group"><label className="form-label">Template Name</label><input className="form-input" value={form.templateName} onChange={e => setForm(f => ({ ...f, templateName: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Subject</label><input className="form-input" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} /><span>Active</span></label>
          <p style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>To edit the full body, images, and signature, use the Email Templates page.</p>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button></div>
      </div>
    </div>
  );
}
